// src/components/PostingPeriod.js
// Posting Period lock (BC "Allow Posting From/To"). Sets a tenant-wide window;
// any GL posting dated outside it is rejected across finance & payables.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Button, Grid, Alert,
  CircularProgress, IconButton, Divider
} from '@mui/material';
import { Home, Lock } from '@mui/icons-material';
import axios from 'axios';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const PostingPeriod = () => {
  const navigate = useNavigate();
  const [from, setFrom]       = useState('');
  const [to, setTo]           = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/api/finance/posting-window`, { headers: hdrs() });
      setFrom(data.from || ''); setTo(data.to || '');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load posting window');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (from && to && to < from) { setError('"To" date cannot be before "From" date'); return; }
    setSaving(true); setError(''); setSuccess('');
    try {
      const { data } = await axios.put(`${API}/api/finance/posting-window`, { from, to }, { headers: hdrs() });
      setFrom(data.from || ''); setTo(data.to || '');
      setSuccess('Posting window saved.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  const clear = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await axios.put(`${API}/api/finance/posting-window`, { from: '', to: '' }, { headers: hdrs() });
      setFrom(''); setTo(''); setSuccess('Posting window cleared — all dates allowed.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to clear');
    } finally { setSaving(false); }
  };

  return (
    <Box sx={{ maxWidth: 620, mx: 'auto', mt: 4, mb: 6 }}>
      <Card elevation={3}>
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <Lock sx={{ mr: 1, color: '#6a1b9a' }} />
              <Typography variant="h5" color="#6a1b9a" fontWeight="bold">Posting Period Lock</Typography>
            </Box>
            <IconButton onClick={() => navigate('/finance')} title="Finance Home"
              sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Home /></IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Restrict the dates on which entries may be posted. Any invoice, receipt, credit note,
            journal, purchase invoice or payment voucher dated <strong>before "From"</strong> or
            <strong> after "To"</strong> will be rejected. Leave a field blank for no bound; clear both to unlock all dates.
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          {loading ? <CircularProgress /> : (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <TextField type="date" label="Allow Posting From" value={from} fullWidth
                  onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }}
                  helperText="Block postings before this date" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField type="date" label="Allow Posting To" value={to} fullWidth
                  onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }}
                  inputProps={{ min: from || undefined }}
                  helperText="Block postings after this date (optional)" />
              </Grid>
              <Grid item xs={12} sx={{ mt: 1 }}>
                <Button variant="contained" onClick={save} disabled={saving} sx={{ mr: 2 }}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
                <Button variant="outlined" color="warning" onClick={clear} disabled={saving}>
                  Clear (unlock all)
                </Button>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PostingPeriod;
